import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/lib/api';

// ── Types ───────────────────────────────────────────────

export type DocumentType =
  | 'LOAN_OFFER'
  | 'DISCHARGE_LETTER'
  | 'VARIATION_NOTICE'
  | 'GUARANTEE'
  | 'SETTLEMENT_LETTER'
  | 'GENERAL';

export type SubmissionStatus =
  'PENDING' | 'PARTIALLY_SIGNED' | 'COMPLETED' | 'EXPIRED' | 'CANCELLED' | 'DECLINED';

export type SignerRole = 'BORROWER' | 'GUARANTOR' | 'WITNESS' | 'DIRECTOR' | 'OTHER';

export interface SigningTemplate {
  id: string;
  name: string;
  documentType: DocumentType;
  description?: string;
  fieldCount: number;
  submissionCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface TemplateField {
  id: string;
  templateId: string;
  name: string;
  type: 'SIGNATURE' | 'TEXT' | 'CHECKBOX' | 'DATE';
  pageNumber: number;
  positionX: number;
  positionY: number;
  width: number;
  height: number;
  required: boolean;
}

export interface Signer {
  id: string;
  name: string;
  email: string;
  role: SignerRole;
  status: 'PENDING' | 'VIEWED' | 'SIGNED' | 'DECLINED';
  signedAt?: string;
  declinedAt?: string;
}

export interface SignatureSubmission {
  id: string;
  templateId: string;
  template?: SigningTemplate;
  loanId?: string;
  loanNumber?: string;
  status: SubmissionStatus;
  signers: Signer[];
  message?: string;
  expiresAt?: string;
  createdAt: string;
  updatedAt: string;
  completedAt?: string;
  declinedAt?: string;
  signingUrl?: string;
}

interface SignatureSubmissionApiResponse {
  id: string;
  templateId: string;
  loanId?: string | null;
  status: SubmissionStatus;
  message?: string | null;
  expiresAt?: string | null;
  createdAt: string;
  updatedAt: string;
  completedAt?: string | null;
  template?: SigningTemplate;
  submitters?: Array<{
    id: string;
    name: string;
    email: string;
    role: SignerRole;
    status: Signer['status'];
    signedAt?: string | null;
    declinedAt?: string | null;
  }>;
  loan?: { loanNumber: string } | null;
}

export interface SubmissionFilters {
  status?: SubmissionStatus;
  loanId?: string;
  templateId?: string;
}

export interface RecentSigningEvent {
  id: string;
  submissionId: string;
  type:
    | 'CREATED'
    | 'VIEWED'
    | 'SIGNED'
    | 'COMPLETED'
    | 'EXPIRED'
    | 'CANCELLED'
    | 'DECLINED'
    | 'REMINDED';
  signerName?: string;
  timestamp: string;
  details?: any;
}

export interface OverdueSubmission {
  id: string;
  loanNumber?: string;
  documentName: string;
  status: SubmissionStatus;
  daysOverdue: number;
  expiresAt: string;
  signers: Signer[];
}

export interface SigningStats {
  totalSent: number;
  completed: number;
  pending: number;
  expired: number;
  avgCompletionDays: number;
}

export interface LoanSigningProgress {
  loanId: string;
  loanNumber: string;
  submissions: SignatureSubmission[];
  overallProgress: number; // 0-100
}

export interface CreateSubmissionInput {
  templateId: string;
  loanId?: string;
  signers: Array<{ name: string; email: string; role: SignerRole }>;
  message?: string;
  expiresAt?: string;
}

export interface SendLoanForSigningInput {
  templateId: string;
  signers: Array<{ name: string; email: string; role: SignerRole }>;
  message?: string;
}

export interface SendDocumentForSigningInput {
  loanId: string;
  documentId: string;
  signers: Array<{ name: string; email: string; role: SignerRole }>;
  message?: string;
  expiresAt?: string;
}

// ── Signing Templates ────────────────────────────────────

async function fetchSigningTemplates(): Promise<SigningTemplate[]> {
  const { data } = await api.get<SigningTemplate[]>('/e-signature/templates');
  return data;
}

export function useSigningTemplates() {
  return useQuery({
    queryKey: ['e-signature', 'templates'],
    queryFn: fetchSigningTemplates,
  });
}

async function fetchSigningTemplate(id: string): Promise<SigningTemplate> {
  const { data } = await api.get<SigningTemplate>(`/e-signature/templates/${id}`);
  return data;
}

export function useSigningTemplate(id: string) {
  return useQuery({
    queryKey: ['e-signature', 'templates', id],
    queryFn: () => fetchSigningTemplate(id),
    enabled: !!id,
  });
}

interface UploadTemplateInput {
  file: File;
  name: string;
  documentType: DocumentType;
  description?: string;
}

async function uploadSigningTemplate(input: UploadTemplateInput): Promise<SigningTemplate> {
  const formData = new FormData();
  formData.append('file', input.file);
  formData.append('name', input.name);
  formData.append('documentType', input.documentType);
  if (input.description) {
    formData.append('description', input.description);
  }

  const { data } = await api.post<SigningTemplate>('/e-signature/templates/upload', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return data;
}

export function useUploadSigningTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: uploadSigningTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'templates'] });
    },
  });
}

async function deleteSigningTemplate(id: string): Promise<void> {
  await api.delete(`/e-signature/templates/${id}`);
}

export function useDeleteSigningTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteSigningTemplate,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'templates'] });
    },
  });
}

async function fetchTemplateFields(id: string): Promise<TemplateField[]> {
  const { data } = await api.get<TemplateField[]>(`/e-signature/templates/${id}/fields`);
  return data;
}

export function useTemplateFields(id: string) {
  return useQuery({
    queryKey: ['e-signature', 'templates', id, 'fields'],
    queryFn: () => fetchTemplateFields(id),
    enabled: !!id,
  });
}

// ── Signature Submissions ────────────────────────────────

async function fetchSignatureSubmissions(
  params?: SubmissionFilters,
): Promise<SignatureSubmission[]> {
  const { data } = await api.get<SignatureSubmissionApiResponse[]>('/e-signature/submissions', {
    params,
  });
  return data.map(submission => ({
    ...submission,
    loanId: submission.loanId ?? undefined,
    loanNumber: submission.loan?.loanNumber,
    message: submission.message ?? undefined,
    expiresAt: submission.expiresAt ?? undefined,
    completedAt: submission.completedAt ?? undefined,
    signers: (submission.submitters ?? []).map(signer => ({
      ...signer,
      signedAt: signer.signedAt ?? undefined,
      declinedAt: signer.declinedAt ?? undefined,
    })),
  }));
}

export function useSignatureSubmissions(params?: SubmissionFilters) {
  return useQuery({
    queryKey: ['e-signature', 'submissions', params],
    queryFn: () => fetchSignatureSubmissions(params),
  });
}

async function fetchSignatureSubmission(id: string): Promise<SignatureSubmission> {
  const { data } = await api.get<SignatureSubmission>(`/e-signature/submissions/${id}`);
  return data;
}

export function useSignatureSubmission(id: string) {
  return useQuery({
    queryKey: ['e-signature', 'submissions', id],
    queryFn: () => fetchSignatureSubmission(id),
    enabled: !!id,
  });
}

async function createSignatureSubmission(
  input: CreateSubmissionInput,
): Promise<SignatureSubmission> {
  const { data } = await api.post<SignatureSubmission>('/e-signature/submissions', {
    templateId: input.templateId,
    loanId: input.loanId,
    submitters: input.signers,
    message: input.message,
    expiresAt: input.expiresAt,
    sendEmail: true,
  });
  return data;
}

export function useCreateSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createSignatureSubmission,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
      if (vars.loanId) {
        qc.invalidateQueries({
          queryKey: ['e-signature', 'loans', vars.loanId, 'signing-progress'],
        });
      }
    },
  });
}

async function sendDocumentForSigning(
  input: SendDocumentForSigningInput,
): Promise<SignatureSubmission> {
  const { data } = await api.post<SignatureSubmission>(
    `/e-signature/loans/${input.loanId}/documents/${input.documentId}/send-for-signing`,
    {
      submitters: input.signers,
      message: input.message,
      expiresAt: input.expiresAt,
      sendEmail: true,
    },
  );
  return data;
}

export function useSendDocumentForSigning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendDocumentForSigning,
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
      qc.invalidateQueries({
        queryKey: ['e-signature', 'loans', vars.loanId, 'signing-progress'],
      });
    },
  });
}

async function cancelSubmission(id: string): Promise<void> {
  await api.post(`/e-signature/submissions/${id}/cancel`);
}

export function useCancelSubmission() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: cancelSubmission,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
    },
  });
}

async function remindSubmitter(id: string): Promise<void> {
  await api.post(`/e-signature/submissions/${id}/remind`);
}

export function useRemindSubmitter() {
  return useMutation({
    mutationFn: remindSubmitter,
  });
}

async function downloadSignedDoc(submissionId: string): Promise<Blob> {
  const { data } = await api.get(`/e-signature/submissions/${submissionId}/download`, {
    responseType: 'blob',
  });
  return data;
}

export function useDownloadSignedDoc(submissionId: string) {
  return useQuery({
    queryKey: ['e-signature', 'submissions', submissionId, 'download'],
    queryFn: () => downloadSignedDoc(submissionId),
    enabled: !!submissionId,
  });
}

async function fetchSubmissionStatus(id: string): Promise<SignatureSubmission> {
  const { data } = await api.get<SignatureSubmission>(`/e-signature/submissions/${id}/status`);
  return data;
}

export function useSubmissionStatus(id: string) {
  return useQuery({
    queryKey: ['e-signature', 'submissions', id, 'status'],
    queryFn: () => fetchSubmissionStatus(id),
    enabled: !!id,
  });
}

// ── Loan Signing ─────────────────────────────────────────

async function sendLoanForSigning(
  loanId: string,
  input: SendLoanForSigningInput,
): Promise<SignatureSubmission> {
  const { data } = await api.post<SignatureSubmission>(
    `/e-signature/loans/${loanId}/send-for-signing`,
    {
      templateId: input.templateId,
      submitters: input.signers,
      message: input.message,
    },
  );
  return data;
}

export function useSendLoanForSigning() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ loanId, ...input }: SendLoanForSigningInput & { loanId: string }) =>
      sendLoanForSigning(loanId, input),
    onSuccess: (_data, vars) => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'loans', vars.loanId, 'signing-progress'] });
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
    },
  });
}

async function fetchLoanSigningProgress(loanId: string): Promise<LoanSigningProgress> {
  const { data } = await api.get<LoanSigningProgress>(
    `/e-signature/loans/${loanId}/signing-progress`,
  );
  return data;
}

export function useLoanSigningProgress(loanId: string) {
  return useQuery({
    queryKey: ['e-signature', 'loans', loanId, 'signing-progress'],
    queryFn: () => fetchLoanSigningProgress(loanId),
    enabled: !!loanId,
  });
}

async function sendLoanOffer(loanId: string): Promise<SignatureSubmission> {
  const { data } = await api.post<SignatureSubmission>(`/e-signature/loans/${loanId}/send-offer`);
  return data;
}

export function useSendLoanOffer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendLoanOffer,
    onSuccess: (_data, loanId) => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'loans', loanId, 'signing-progress'] });
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
    },
  });
}

async function sendDischarge(loanId: string): Promise<SignatureSubmission> {
  const { data } = await api.post<SignatureSubmission>(
    `/e-signature/loans/${loanId}/send-discharge`,
  );
  return data;
}

export function useSendDischarge() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: sendDischarge,
    onSuccess: (_data, loanId) => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'loans', loanId, 'signing-progress'] });
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
    },
  });
}

// ── Overdue & Bulk Operations ────────────────────────────

async function fetchOverdueSignatures(days: number = 7): Promise<OverdueSubmission[]> {
  const { data } = await api.get<OverdueSubmission[]>('/e-signature/overdue-signatures', {
    params: { daysOverdue: days },
  });
  return data;
}

export function useOverdueSignatures(days: number = 7) {
  return useQuery({
    queryKey: ['e-signature', 'overdue', days],
    queryFn: () => fetchOverdueSignatures(days),
  });
}

interface BulkRemindInput {
  submissionIds: string[];
}

async function bulkRemind(input: BulkRemindInput): Promise<void> {
  await api.post('/e-signature/bulk-remind', input);
}

export function useBulkRemind() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: bulkRemind,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['e-signature', 'submissions'] });
      qc.invalidateQueries({ queryKey: ['e-signature', 'overdue'] });
    },
  });
}

async function fetchSigningStats(): Promise<SigningStats> {
  const { data } = await api.get<SigningStats>('/e-signature/stats');
  return data;
}

export function useSigningStats() {
  return useQuery({
    queryKey: ['e-signature', 'stats'],
    queryFn: fetchSigningStats,
  });
}
